// Unified WebGL post-processing: Bloom (Dual Kawase) + All Effects
// Single reusable system for gameplay canvas and splash screen
// Architecture: Bloom FIRST (multi-pass) → Composite shader (all effects)

import { getTheme } from "../visuals/theme";

export interface UnifiedPostProcessConfig {
  // Bloom configuration
  bloom: {
    enabled: boolean;
    intensity: number;
    threshold: number;
    iterations: number;
  };

  // Other effects configuration
  chromaticAberration: {
    enabled: boolean;
    intensity: number;
  };

  motionBlur: {
    enabled: boolean;
    samples: number;
    intensity: number;
  };

  radialBlur: {
    enabled: boolean;
    intensity: number;
    centerX: number;
    centerY: number;
  };

  colorGrade: {
    enabled: boolean;
    saturation: number;
    contrast: number;
    brightness: number;
  };

  pixelation: {
    enabled: boolean;
    pixelSize: number;
  };

  palette: {
    enabled: boolean;
    ditherEnabled: boolean;
  };

  flash: {
    intensity: number;
    color: [number, number, number];
  };

  distortion: {
    intensity: number;
    centerX: number;
    centerY: number;
    radius: number;
  };

  heatDistortion: {
    intensity: number;
    centerX: number;
    centerY: number;
    radius: number;
  };

  screenShake: {
    offsetX: number;
    offsetY: number;
    rotation: number;
  };
}

function getDefaultConfig(): UnifiedPostProcessConfig {
  const theme = getTheme();
  return {
    bloom: {
      enabled: theme.postprocess.bloom.enabled,
      intensity: theme.postprocess.bloom.intensity,
      threshold: theme.postprocess.bloom.threshold,
      iterations: theme.postprocess.bloom.iterations,
    },
    chromaticAberration: {
      enabled: theme.postprocess.chromaticAberration.enabled,
      intensity: theme.postprocess.chromaticAberration.intensity,
    },
    pixelation: {
      enabled: theme.postprocess.pixelation.enabled,
      pixelSize: theme.postprocess.pixelation.pixelSize,
    },
    palette: {
      enabled: theme.postprocess.palette.enabled,
      ditherEnabled: theme.postprocess.palette.ditherEnabled,
    },
    colorGrade: {
      enabled: theme.postprocess.colorGrade.enabled,
      saturation: theme.postprocess.colorGrade.saturation,
      contrast: theme.postprocess.colorGrade.contrast,
      brightness: theme.postprocess.colorGrade.brightness,
    },
    radialBlur: { enabled: false, intensity: 0, centerX: 0.5, centerY: 0.5 },
    motionBlur: { enabled: false, samples: 4, intensity: 0.5 },
    flash: { intensity: 0, color: [1, 1, 1] },
    distortion: { intensity: 0, centerX: 0.5, centerY: 0.5, radius: 0.1 },
    heatDistortion: { intensity: 0, centerX: 0.5, centerY: 0.5, radius: 0.2 },
    screenShake: { offsetX: 0, offsetY: 0, rotation: 0 },
  };
}

export class UnifiedPostProcessEffects {
  private gl: WebGLRenderingContext;
  private glCanvas: HTMLCanvasElement;
  private config: UnifiedPostProcessConfig;
  private width: number;
  private height: number;

  // Bloom shaders (separate passes)
  private thresholdShader: ShaderProgram;
  private downsampleShader: ShaderProgram;
  private upsampleShader: ShaderProgram;

  // Composite shader (bloom + all effects)
  private compositeShader: ShaderProgram;

  // Bloom framebuffers and textures (mip chain)
  private bloomFramebuffers: WebGLFramebuffer[] = [];
  private bloomTextures: WebGLTexture[] = [];
  private sourceTexture!: WebGLTexture;

  private quadBuffer: WebGLBuffer;
  private time = 0;

  constructor(
    width: number,
    height: number,
    config: Partial<UnifiedPostProcessConfig> = {},
  ) {
    this.config = this.mergeConfig(getDefaultConfig(), config);
    this.width = width;
    this.height = height;

    this.glCanvas = document.createElement("canvas");
    this.glCanvas.width = width;
    this.glCanvas.height = height;

    const gl = this.glCanvas.getContext("webgl", {
      alpha: true,
      preserveDrawingBuffer: true,
    });
    if (!gl) throw new Error("WebGL not supported");
    this.gl = gl;

    // Create shaders
    this.thresholdShader = this.createThresholdShader();
    this.downsampleShader = this.createDownsampleShader();
    this.upsampleShader = this.createUpsampleShader();
    this.compositeShader = this.createCompositeShader();

    // Create quad buffer for fullscreen rendering
    this.quadBuffer = this.createQuadBuffer();

    // Create source texture for input
    this.sourceTexture = this.createTexture(width, height)!;

    // Create bloom framebuffer chain
    this.createBloomFramebufferChain();
  }

  /**
   * Apply all post-processing effects to source canvas
   * 1. Bloom (threshold → downsample → upsample)
   * 2. Composite (bloom + base + all other effects)
   */
  applyEffects(
    sourceCanvas: HTMLCanvasElement,
    targetCtx: CanvasRenderingContext2D,
  ): void {
    const gl = this.gl;
    this.time += 0.016;

    // Upload source canvas to texture
    gl.bindTexture(gl.TEXTURE_2D, this.sourceTexture);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
    gl.texImage2D(
      gl.TEXTURE_2D,
      0,
      gl.RGBA,
      gl.RGBA,
      gl.UNSIGNED_BYTE,
      sourceCanvas,
    );

    // === BLOOM PASSES (if enabled) ===
    let bloomTexture: WebGLTexture | null = null;

    if (this.config.bloom.enabled) {
      // Clear all bloom framebuffers to prevent accumulation.
      // Note: renderToFramebuffer() also calls gl.clear(), but the viewport may not be set correctly
      // at that moment (it's set inside the render lambda). Explicit clearing with correct viewport
      // is required to prevent bloom persistence bugs.
      this.clearBloomFramebuffers();

      // 1. Threshold pass: Extract bright areas
      this.renderToFramebuffer(this.bloomFramebuffers[0]!, () => {
        this.activateShader(this.thresholdShader);
        gl.uniform1f(
          this.thresholdShader.uniforms.threshold!,
          this.config.bloom.threshold,
        );
        this.bindTexture(this.sourceTexture, 0);
        this.drawQuad();
      });

      // 2. Downsample passes
      for (let i = 0; i < this.config.bloom.iterations; i++) {
        const sourceTexture = this.bloomTextures[i]!;
        const targetFB = this.bloomFramebuffers[i + 1]!;
        const mipSize = this.getMipSize(i + 1);

        this.renderToFramebuffer(targetFB, () => {
          gl.viewport(0, 0, mipSize.width, mipSize.height);
          this.activateShader(this.downsampleShader);
          gl.uniform2f(
            this.downsampleShader.uniforms.texelSize!,
            1 / mipSize.width,
            1 / mipSize.height,
          );
          this.bindTexture(sourceTexture, 0);
          this.drawQuad();
        });
      }

      // 3. Upsample passes (additive blending)
      for (let i = this.config.bloom.iterations - 1; i >= 0; i--) {
        const sourceTexture = this.bloomTextures[i + 1]!;
        const targetFB = this.bloomFramebuffers[i]!;
        const mipSize = this.getMipSize(i);

        this.renderToFramebuffer(targetFB, () => {
          gl.viewport(0, 0, mipSize.width, mipSize.height);
          gl.enable(gl.BLEND);
          gl.blendFunc(gl.ONE, gl.ONE);
          this.activateShader(this.upsampleShader);
          gl.uniform2f(
            this.upsampleShader.uniforms.texelSize!,
            1 / mipSize.width,
            1 / mipSize.height,
          );
          this.bindTexture(sourceTexture, 0);
          this.drawQuad();
          gl.disable(gl.BLEND);
        });
      }

      bloomTexture = this.bloomTextures[0]!;
    }

    // === COMPOSITE PASS: Bloom + Base + All Effects ===
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, this.width, this.height);
    gl.clear(gl.COLOR_BUFFER_BIT);

    this.activateShader(this.compositeShader);
    this.setCompositeUniforms();

    // Bind base texture to unit 0
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.sourceTexture);
    gl.uniform1i(this.compositeShader.uniforms.uBaseTexture!, 0);

    // Bind bloom texture to unit 1 (or use base if bloom disabled)
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, bloomTexture || this.sourceTexture);
    gl.uniform1i(this.compositeShader.uniforms.uBloomTexture!, 1);

    this.drawQuad();

    // Present to target canvas
    targetCtx.drawImage(this.glCanvas, 0, 0, this.width, this.height);
  }

  setConfig(config: Partial<UnifiedPostProcessConfig>): void {
    this.config = this.mergeConfig(this.config, config);
  }

  flash(
    intensity: number = 1.0,
    color: [number, number, number] = [1, 1, 1],
  ): void {
    this.config.flash.intensity = intensity;
    this.config.flash.color = color;
  }

  distort(
    x: number,
    y: number,
    intensity: number = 1.0,
    radius: number = 0.1,
  ): void {
    this.config.distortion.centerX = x;
    this.config.distortion.centerY = y;
    this.config.distortion.intensity = intensity;
    this.config.distortion.radius = radius;
  }

  heatDistort(
    x: number,
    y: number,
    intensity: number = 1.0,
    radius: number = 0.2,
  ): void {
    this.config.heatDistortion.centerX = x;
    this.config.heatDistortion.centerY = y;
    this.config.heatDistortion.intensity = intensity;
    this.config.heatDistortion.radius = radius;
  }

  shake(intensity: number = 10): void {
    this.config.screenShake.offsetX = (Math.random() - 0.5) * intensity;
    this.config.screenShake.offsetY = (Math.random() - 0.5) * intensity;
    this.config.screenShake.rotation = (Math.random() - 0.5) * 0.02 * intensity;
  }

  resize(width: number, height: number): void {
    this.width = width;
    this.height = height;

    this.glCanvas.width = width;
    this.glCanvas.height = height;

    const gl = this.gl;

    // Recreate source texture
    gl.deleteTexture(this.sourceTexture);
    this.sourceTexture = this.createTexture(width, height)!;

    // Recreate bloom framebuffer chain
    for (const fb of this.bloomFramebuffers) {
      gl.deleteFramebuffer(fb);
    }
    for (const tex of this.bloomTextures) {
      gl.deleteTexture(tex);
    }
    this.bloomFramebuffers = [];
    this.bloomTextures = [];
    this.createBloomFramebufferChain();
  }

  // ============================================================================
  // SHADER CREATION
  // ============================================================================

  private createThresholdShader(): ShaderProgram {
    const vertSrc = `
      attribute vec2 position;
      varying vec2 vUv;
      void main() {
        vUv = position * 0.5 + 0.5;
        gl_Position = vec4(position, 0.0, 1.0);
      }
    `;

    const fragSrc = `
      precision mediump float;
      varying vec2 vUv;
      uniform sampler2D uTexture;
      uniform float threshold;
      void main() {
        vec4 color = texture2D(uTexture, vUv);
        float luminance = dot(color.rgb, vec3(0.299, 0.587, 0.114));

        // Smooth threshold with soft rolloff
        float knee = 0.2;
        float brightness = smoothstep(threshold - knee, threshold + knee, luminance);
        brightness = pow(brightness, 1.5); // Gentle curve for natural falloff

        gl_FragColor = vec4(clamp(color.rgb * brightness / max(luminance, 0.01), 0.0, 1.0), color.a);
      }
    `;

    return this.compileShaderProgram(vertSrc, fragSrc, [
      "uTexture",
      "threshold",
    ]);
  }

  private createDownsampleShader(): ShaderProgram {
    const vertSrc = `
      attribute vec2 position;
      varying vec2 vUv;
      void main() {
        vUv = position * 0.5 + 0.5;
        gl_Position = vec4(position, 0.0, 1.0);
      }
    `;

    const fragSrc = `
      precision mediump float;
      varying vec2 vUv;
      uniform sampler2D uTexture;
      uniform vec2 texelSize;
      void main() {
        // 13-tap Gaussian downsample for smooth bloom
        vec4 sum = vec4(0.0);

        // Center
        sum += texture2D(uTexture, vUv) * 0.196;

        // Inner ring (1 pixel)
        sum += texture2D(uTexture, vUv + vec2(-1.0, -1.0) * texelSize) * 0.078;
        sum += texture2D(uTexture, vUv + vec2( 0.0, -1.0) * texelSize) * 0.098;
        sum += texture2D(uTexture, vUv + vec2( 1.0, -1.0) * texelSize) * 0.078;
        sum += texture2D(uTexture, vUv + vec2(-1.0,  0.0) * texelSize) * 0.098;
        sum += texture2D(uTexture, vUv + vec2( 1.0,  0.0) * texelSize) * 0.098;
        sum += texture2D(uTexture, vUv + vec2(-1.0,  1.0) * texelSize) * 0.078;
        sum += texture2D(uTexture, vUv + vec2( 0.0,  1.0) * texelSize) * 0.098;
        sum += texture2D(uTexture, vUv + vec2( 1.0,  1.0) * texelSize) * 0.078;

        // Outer ring (2 pixels)
        sum += texture2D(uTexture, vUv + vec2(-2.0,  0.0) * texelSize) * 0.024;
        sum += texture2D(uTexture, vUv + vec2( 2.0,  0.0) * texelSize) * 0.024;
        sum += texture2D(uTexture, vUv + vec2( 0.0, -2.0) * texelSize) * 0.024;
        sum += texture2D(uTexture, vUv + vec2( 0.0,  2.0) * texelSize) * 0.024;

        gl_FragColor = sum;
      }
    `;

    return this.compileShaderProgram(vertSrc, fragSrc, [
      "uTexture",
      "texelSize",
    ]);
  }

  private createUpsampleShader(): ShaderProgram {
    const vertSrc = `
      attribute vec2 position;
      varying vec2 vUv;
      void main() {
        vUv = position * 0.5 + 0.5;
        gl_Position = vec4(position, 0.0, 1.0);
      }
    `;

    const fragSrc = `
      precision mediump float;
      varying vec2 vUv;
      uniform sampler2D uTexture;
      uniform vec2 texelSize;
      void main() {
        // 9-tap tent filter for smooth upsampling
        vec4 sum = vec4(0.0);

        // Center
        sum += texture2D(uTexture, vUv) * 0.25;

        // Cross pattern
        sum += texture2D(uTexture, vUv + vec2( 0.0, -1.0) * texelSize) * 0.125;
        sum += texture2D(uTexture, vUv + vec2(-1.0,  0.0) * texelSize) * 0.125;
        sum += texture2D(uTexture, vUv + vec2( 1.0,  0.0) * texelSize) * 0.125;
        sum += texture2D(uTexture, vUv + vec2( 0.0,  1.0) * texelSize) * 0.125;

        // Corners
        sum += texture2D(uTexture, vUv + vec2(-1.0, -1.0) * texelSize) * 0.0625;
        sum += texture2D(uTexture, vUv + vec2( 1.0, -1.0) * texelSize) * 0.0625;
        sum += texture2D(uTexture, vUv + vec2(-1.0,  1.0) * texelSize) * 0.0625;
        sum += texture2D(uTexture, vUv + vec2( 1.0,  1.0) * texelSize) * 0.0625;

        gl_FragColor = sum;
      }
    `;

    return this.compileShaderProgram(vertSrc, fragSrc, [
      "uTexture",
      "texelSize",
    ]);
  }

  private createCompositeShader(): ShaderProgram {
    const vertSrc = `
      attribute vec2 position;
      varying vec2 vUv;
      void main() {
        vUv = position * 0.5 + 0.5;
        gl_Position = vec4(position, 0.0, 1.0);
      }
    `;

    const fragSrc = `
      precision highp float;
      varying vec2 vUv;

      uniform sampler2D uBaseTexture;
      uniform sampler2D uBloomTexture;
      uniform vec2 uResolution;
      uniform float uTime;

      // Bloom
      uniform bool uBloomEnabled;
      uniform float uBloomIntensity;

      // Effects
      uniform bool uChromaticEnabled;
      uniform float uChromaticIntensity;

      uniform bool uColorGradeEnabled;
      uniform float uSaturation;
      uniform float uContrast;
      uniform float uBrightness;

      uniform bool uPixelationEnabled;
      uniform float uPixelSize;

      uniform bool uPaletteEnabled;
      uniform bool uDitherEnabled;

      uniform float uFlashIntensity;
      uniform vec3 uFlashColor;

      uniform float uDistortionIntensity;
      uniform vec2 uDistortionCenter;
      uniform float uDistortionRadius;

      uniform float uHeatDistortionIntensity;
      uniform vec2 uHeatDistortionCenter;
      uniform float uHeatDistortionRadius;

      uniform bool uRadialBlurEnabled;
      uniform float uRadialBlurIntensity;
      uniform vec2 uRadialBlurCenter;

      uniform vec2 uShakeOffset;
      uniform float uShakeRotation;

      float random(vec2 co) {
        return fract(sin(dot(co.xy, vec2(12.9898, 78.233))) * 43758.5453);
      }

      vec2 snapToPixelGrid(vec2 uv, float pixelSize) {
        vec2 texel = 1.0 / uResolution;
        vec2 step = texel * pixelSize;
        return floor(uv / step) * step + 0.5 * step;
      }

      float bayer4(vec2 screenPos) {
        vec2 pos = mod(screenPos, 4.0);
        float x = pos.x;
        float y = pos.y;

        if (y < 1.0) {
          if (x < 1.0) return (0.0 + 0.5) / 16.0;
          if (x < 2.0) return (8.0 + 0.5) / 16.0;
          if (x < 3.0) return (2.0 + 0.5) / 16.0;
          return (10.0 + 0.5) / 16.0;
        }
        if (y < 2.0) {
          if (x < 1.0) return (12.0 + 0.5) / 16.0;
          if (x < 2.0) return (4.0 + 0.5) / 16.0;
          if (x < 3.0) return (14.0 + 0.5) / 16.0;
          return (6.0 + 0.5) / 16.0;
        }
        if (y < 3.0) {
          if (x < 1.0) return (3.0 + 0.5) / 16.0;
          if (x < 2.0) return (11.0 + 0.5) / 16.0;
          if (x < 3.0) return (1.0 + 0.5) / 16.0;
          return (9.0 + 0.5) / 16.0;
        }
        if (x < 1.0) return (15.0 + 0.5) / 16.0;
        if (x < 2.0) return (7.0 + 0.5) / 16.0;
        if (x < 3.0) return (13.0 + 0.5) / 16.0;
        return (5.0 + 0.5) / 16.0;
      }

      vec3 getPaletteColor(int index) {
        if (index == 0) return vec3(1.0, 1.0, 1.0);
        if (index == 1) return vec3(0.455, 1.0, 0.353);
        if (index == 2) return vec3(0.0, 0.898, 1.0);
        if (index == 3) return vec3(1.0, 0.831, 0.0);
        if (index == 4) return vec3(0.77, 0.0, 1.0);
        if (index == 5) return vec3(0.78, 0.78, 0.78);
        if (index == 6) return vec3(0.42, 0.42, 0.42);
        return vec3(0.15, 0.15, 0.15);
      }

      vec3 quantizeToPalette(vec3 color) {
        float bestDistance = 1e9;
        vec3 bestColor = color;
        vec3 luminanceWeight = vec3(0.2126, 0.7152, 0.0722);

        for (int i = 0; i < 8; i++) {
          vec3 paletteColor = getPaletteColor(i);
          vec3 diff = color - paletteColor;
          float distance = dot(diff * diff, luminanceWeight);
          if (distance < bestDistance) {
            bestDistance = distance;
            bestColor = paletteColor;
          }
        }
        return bestColor;
      }

      vec3 rgb2hsv(vec3 c) {
        vec4 K = vec4(0.0, -1.0 / 3.0, 2.0 / 3.0, -1.0);
        vec4 p = mix(vec4(c.bg, K.wz), vec4(c.gb, K.xy), step(c.b, c.g));
        vec4 q = mix(vec4(p.xyw, c.r), vec4(c.r, p.yzx), step(p.x, c.r));
        float d = q.x - min(q.w, q.y);
        float e = 1.0e-10;
        return vec3(abs(q.z + (q.w - q.y) / (6.0 * d + e)), d / (q.x + e), q.x);
      }

      vec3 hsv2rgb(vec3 c) {
        vec4 K = vec4(1.0, 2.0 / 3.0, 1.0 / 3.0, 3.0);
        vec3 p = abs(fract(c.xxx + K.xyz) * 6.0 - K.www);
        return c.z * mix(K.xxx, clamp(p - K.xxx, 0.0, 1.0), c.y);
      }

      void main() {
        vec2 uv = vUv;

        // Screen shake transform
        if (length(uShakeOffset) > 0.0 || abs(uShakeRotation) > 0.0) {
          vec2 shakeUV = uv - 0.5;
          float c = cos(uShakeRotation);
          float s = sin(uShakeRotation);
          shakeUV = vec2(
            shakeUV.x * c - shakeUV.y * s,
            shakeUV.x * s + shakeUV.y * c
          );
          shakeUV += uShakeOffset / uResolution;
          uv = shakeUV + 0.5;
        }

        // Pixelation
        vec2 samplingUV = uv;
        if (uPixelationEnabled) {
          samplingUV = snapToPixelGrid(uv, uPixelSize);
        }

        // Distortion effects
        if (uDistortionIntensity > 0.0) {
          vec2 toCenter = uv - uDistortionCenter;
          float dist = length(toCenter);
          float wave = sin(dist / uDistortionRadius * 3.14159 - uTime * 10.0) * 0.5 + 0.5;
          float distortAmount = uDistortionIntensity * wave * smoothstep(uDistortionRadius * 2.0, 0.0, dist);
          uv += normalize(toCenter) * distortAmount * 0.05;
        }

        if (uHeatDistortionIntensity > 0.0) {
          vec2 toCenter = uv - uHeatDistortionCenter;
          float dist = length(toCenter);
          float falloff = smoothstep(uHeatDistortionRadius, 0.0, dist);
          float noise1 = sin(uv.x * 50.0 + uTime * 10.0) * cos(uv.y * 30.0 + uTime * 8.0);
          float noise2 = sin(uv.x * 30.0 - uTime * 7.0) * cos(uv.y * 50.0 - uTime * 11.0);
          vec2 heatOffset = vec2(noise1, noise2) * uHeatDistortionIntensity * falloff * 0.01;
          uv += heatOffset;
        }

        // Sample base + bloom (with optional effects)
        vec4 color;

        if (uRadialBlurEnabled && uRadialBlurIntensity > 0.0) {
          vec2 dir = samplingUV - uRadialBlurCenter;
          vec4 sum = vec4(0.0);
          for (int i = 0; i < 8; i++) {
            float t = float(i) / 8.0;
            vec2 offset = dir * t * uRadialBlurIntensity * 0.02;
            vec4 base = texture2D(uBaseTexture, samplingUV - offset);
            vec4 bloom = texture2D(uBloomTexture, samplingUV - offset);

            // Screen blend for radial blur
            if (uBloomEnabled) {
              vec3 bloomColor = bloom.rgb * uBloomIntensity;
              vec3 blended = base.rgb + bloomColor - base.rgb * bloomColor;
              sum += vec4(blended, base.a);
            } else {
              sum += base;
            }
          }
          color = sum / 8.0;
        } else if (uChromaticEnabled) {
          float offset = uChromaticIntensity / uResolution.x;
          vec2 dir = normalize(samplingUV - vec2(0.5));

          vec4 baseR = texture2D(uBaseTexture, samplingUV + dir * offset);
          vec4 baseG = texture2D(uBaseTexture, samplingUV);
          vec4 baseB = texture2D(uBaseTexture, samplingUV - dir * offset);

          vec4 bloomR = texture2D(uBloomTexture, samplingUV + dir * offset);
          vec4 bloomG = texture2D(uBloomTexture, samplingUV);
          vec4 bloomB = texture2D(uBloomTexture, samplingUV - dir * offset);

          // Screen blend for chromatic bloom
          float r = baseR.r;
          float g = baseG.g;
          float b = baseB.b;

          if (uBloomEnabled) {
            float bloomIntR = bloomR.r * uBloomIntensity;
            float bloomIntG = bloomG.g * uBloomIntensity;
            float bloomIntB = bloomB.b * uBloomIntensity;
            r = r + bloomIntR - r * bloomIntR;
            g = g + bloomIntG - g * bloomIntG;
            b = b + bloomIntB - b * bloomIntB;
          }

          color = vec4(r, g, b, 1.0);
        } else {
          vec4 base = texture2D(uBaseTexture, samplingUV);
          vec4 bloom = texture2D(uBloomTexture, samplingUV);

          // Screen blend mode for more natural bloom
          if (uBloomEnabled) {
            vec3 bloomColor = bloom.rgb * uBloomIntensity;
            color.rgb = base.rgb + bloomColor - base.rgb * bloomColor;
            color.a = base.a;
          } else {
            color = base;
          }
        }

        // Palette quantization with dithering
        if (uPaletteEnabled) {
          vec3 originalColor = color.rgb;
          if (uDitherEnabled) {
            vec2 screenPos = uv * uResolution;
            float luminance = dot(originalColor, vec3(0.2126, 0.7152, 0.0722));
            float ditherThreshold = bayer4(screenPos);
            float quantizedLuma = floor(luminance * 7.0 + ditherThreshold) / 7.0;
            vec3 colorDirection = normalize(originalColor + vec3(1e-5));
            vec3 ditherApprox = colorDirection * quantizedLuma;
            color.rgb = quantizeToPalette(ditherApprox);
          } else {
            color.rgb = quantizeToPalette(originalColor);
          }
        }

        // Color grading
        if (uColorGradeEnabled) {
          vec3 hsv = rgb2hsv(color.rgb);
          hsv.y *= uSaturation;
          hsv.z *= uBrightness;
          color.rgb = hsv2rgb(hsv);
          color.rgb = (color.rgb - 0.5) * uContrast + 0.5;
        }

        // Flash overlay
        if (uFlashIntensity > 0.0) {
          color.rgb = mix(color.rgb, uFlashColor, uFlashIntensity);
        }

        gl_FragColor = color;
      }
    `;

    const uniformNames = [
      "uBaseTexture",
      "uBloomTexture",
      "uResolution",
      "uTime",
      "uBloomEnabled",
      "uBloomIntensity",
      "uChromaticEnabled",
      "uChromaticIntensity",
      "uColorGradeEnabled",
      "uSaturation",
      "uContrast",
      "uBrightness",
      "uPixelationEnabled",
      "uPixelSize",
      "uPaletteEnabled",
      "uDitherEnabled",
      "uFlashIntensity",
      "uFlashColor",
      "uDistortionIntensity",
      "uDistortionCenter",
      "uDistortionRadius",
      "uHeatDistortionIntensity",
      "uHeatDistortionCenter",
      "uHeatDistortionRadius",
      "uRadialBlurEnabled",
      "uRadialBlurIntensity",
      "uRadialBlurCenter",
      "uShakeOffset",
      "uShakeRotation",
    ];

    return this.compileShaderProgram(vertSrc, fragSrc, uniformNames);
  }

  // ============================================================================
  // UNIFORM SETUP
  // ============================================================================

  private setCompositeUniforms(): void {
    const gl = this.gl;
    const shader = this.compositeShader;
    const cfg = this.config;

    gl.uniform2f(shader.uniforms.uResolution!, this.width, this.height);
    gl.uniform1f(shader.uniforms.uTime!, this.time);

    // Bloom
    gl.uniform1i(shader.uniforms.uBloomEnabled!, cfg.bloom.enabled ? 1 : 0);
    gl.uniform1f(shader.uniforms.uBloomIntensity!, cfg.bloom.intensity);

    // Chromatic aberration
    gl.uniform1i(
      shader.uniforms.uChromaticEnabled!,
      cfg.chromaticAberration.enabled ? 1 : 0,
    );
    gl.uniform1f(
      shader.uniforms.uChromaticIntensity!,
      cfg.chromaticAberration.intensity,
    );

    // Color grading
    gl.uniform1i(
      shader.uniforms.uColorGradeEnabled!,
      cfg.colorGrade.enabled ? 1 : 0,
    );
    gl.uniform1f(shader.uniforms.uSaturation!, cfg.colorGrade.saturation);
    gl.uniform1f(shader.uniforms.uContrast!, cfg.colorGrade.contrast);
    gl.uniform1f(shader.uniforms.uBrightness!, cfg.colorGrade.brightness);

    // Pixelation
    gl.uniform1i(
      shader.uniforms.uPixelationEnabled!,
      cfg.pixelation.enabled ? 1 : 0,
    );
    gl.uniform1f(shader.uniforms.uPixelSize!, cfg.pixelation.pixelSize);

    // Palette quantization
    gl.uniform1i(shader.uniforms.uPaletteEnabled!, cfg.palette.enabled ? 1 : 0);
    gl.uniform1i(
      shader.uniforms.uDitherEnabled!,
      cfg.palette.ditherEnabled ? 1 : 0,
    );

    // Flash
    gl.uniform1f(shader.uniforms.uFlashIntensity!, cfg.flash.intensity);
    gl.uniform3f(
      shader.uniforms.uFlashColor!,
      cfg.flash.color[0],
      cfg.flash.color[1],
      cfg.flash.color[2],
    );

    // Distortion
    gl.uniform1f(
      shader.uniforms.uDistortionIntensity!,
      cfg.distortion.intensity,
    );
    gl.uniform2f(
      shader.uniforms.uDistortionCenter!,
      cfg.distortion.centerX,
      cfg.distortion.centerY,
    );
    gl.uniform1f(shader.uniforms.uDistortionRadius!, cfg.distortion.radius);

    // Heat distortion
    gl.uniform1f(
      shader.uniforms.uHeatDistortionIntensity!,
      cfg.heatDistortion.intensity,
    );
    gl.uniform2f(
      shader.uniforms.uHeatDistortionCenter!,
      cfg.heatDistortion.centerX,
      cfg.heatDistortion.centerY,
    );
    gl.uniform1f(
      shader.uniforms.uHeatDistortionRadius!,
      cfg.heatDistortion.radius,
    );

    // Radial blur
    gl.uniform1i(
      shader.uniforms.uRadialBlurEnabled!,
      cfg.radialBlur.enabled ? 1 : 0,
    );
    gl.uniform1f(
      shader.uniforms.uRadialBlurIntensity!,
      cfg.radialBlur.intensity,
    );
    gl.uniform2f(
      shader.uniforms.uRadialBlurCenter!,
      cfg.radialBlur.centerX,
      cfg.radialBlur.centerY,
    );

    // Screen shake
    gl.uniform2f(
      shader.uniforms.uShakeOffset!,
      cfg.screenShake.offsetX,
      cfg.screenShake.offsetY,
    );
    gl.uniform1f(shader.uniforms.uShakeRotation!, cfg.screenShake.rotation);

    // Auto-decay transient effects
    if (cfg.flash.intensity > 0) {
      cfg.flash.intensity = Math.max(0, cfg.flash.intensity - 0.05);
    }
    if (cfg.distortion.intensity > 0) {
      cfg.distortion.intensity = Math.max(0, cfg.distortion.intensity - 0.03);
    }
    if (cfg.heatDistortion.intensity > 0) {
      cfg.heatDistortion.intensity = Math.max(
        0,
        cfg.heatDistortion.intensity - 0.02,
      );
    }
    cfg.screenShake.offsetX *= 0.85;
    cfg.screenShake.offsetY *= 0.85;
    cfg.screenShake.rotation *= 0.85;
  }

  // ============================================================================
  // WEBGL UTILITIES
  // ============================================================================

  private compileShaderProgram(
    vertSrc: string,
    fragSrc: string,
    uniformNames: string[],
  ): ShaderProgram {
    const gl = this.gl;

    const vertShader = this.compileShader(gl.VERTEX_SHADER, vertSrc);
    const fragShader = this.compileShader(gl.FRAGMENT_SHADER, fragSrc);

    const program = gl.createProgram()!;
    gl.attachShader(program, vertShader);
    gl.attachShader(program, fragShader);
    gl.linkProgram(program);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      throw new Error(`Shader link error: ${gl.getProgramInfoLog(program)}`);
    }

    const uniforms: Record<string, WebGLUniformLocation> = {};
    for (const name of uniformNames) {
      const loc = gl.getUniformLocation(program, name);
      if (loc) uniforms[name] = loc;
    }

    const positionAttrib = gl.getAttribLocation(program, "position");

    return { program, uniforms, positionAttrib };
  }

  private compileShader(type: number, source: string): WebGLShader {
    const gl = this.gl;
    const shader = gl.createShader(type)!;
    gl.shaderSource(shader, source);
    gl.compileShader(shader);

    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      throw new Error(`Shader compile error: ${gl.getShaderInfoLog(shader)}`);
    }

    return shader;
  }

  private createQuadBuffer(): WebGLBuffer {
    const gl = this.gl;
    const positions = new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]);

    const buffer = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, positions, gl.STATIC_DRAW);

    return buffer;
  }

  private createTexture(width: number, height: number): WebGLTexture | null {
    const gl = this.gl;
    const texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texImage2D(
      gl.TEXTURE_2D,
      0,
      gl.RGBA,
      width,
      height,
      0,
      gl.RGBA,
      gl.UNSIGNED_BYTE,
      null,
    );
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    return texture;
  }

  private createBloomFramebufferChain(): void {
    const gl = this.gl;

    for (let i = 0; i <= this.config.bloom.iterations; i++) {
      const mipSize = this.getMipSize(i);
      const texture = this.createTexture(mipSize.width, mipSize.height)!;
      const framebuffer = gl.createFramebuffer()!;

      gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer);
      gl.framebufferTexture2D(
        gl.FRAMEBUFFER,
        gl.COLOR_ATTACHMENT0,
        gl.TEXTURE_2D,
        texture,
        0,
      );

      // Check framebuffer completeness
      const status = gl.checkFramebufferStatus(gl.FRAMEBUFFER);
      if (status !== gl.FRAMEBUFFER_COMPLETE) {
        console.error(
          `Framebuffer incomplete at bloom iteration ${i}: status 0x${status.toString(16)}`,
        );
      }

      this.bloomTextures[i] = texture;
      this.bloomFramebuffers[i] = framebuffer;
    }

    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  }

  private clearBloomFramebuffers(): void {
    const gl = this.gl;

    // Clear all bloom framebuffers to prevent accumulation between frames
    for (let i = 0; i <= this.config.bloom.iterations; i++) {
      const mipSize = this.getMipSize(i);
      gl.bindFramebuffer(gl.FRAMEBUFFER, this.bloomFramebuffers[i]!);
      gl.viewport(0, 0, mipSize.width, mipSize.height);
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
    }

    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  }

  private getMipSize(level: number): { width: number; height: number } {
    const scale = 2 ** level;
    return {
      width: Math.max(1, Math.floor(this.width / scale)),
      height: Math.max(1, Math.floor(this.height / scale)),
    };
  }

  private activateShader(shader: ShaderProgram): void {
    const gl = this.gl;
    gl.useProgram(shader.program);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.quadBuffer);
    gl.enableVertexAttribArray(shader.positionAttrib);
    gl.vertexAttribPointer(shader.positionAttrib, 2, gl.FLOAT, false, 0, 0);
  }

  private bindTexture(texture: WebGLTexture | undefined, unit: number): void {
    if (!texture) return;
    const gl = this.gl;
    gl.activeTexture(gl.TEXTURE0 + unit);
    gl.bindTexture(gl.TEXTURE_2D, texture);
  }

  private drawQuad(): void {
    this.gl.drawArrays(this.gl.TRIANGLE_STRIP, 0, 4);
  }

  private renderToFramebuffer(
    fb: WebGLFramebuffer | undefined,
    renderFn: () => void,
  ): void {
    if (!fb) return;
    const gl = this.gl;
    gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
    gl.clear(gl.COLOR_BUFFER_BIT);
    renderFn();
  }

  private mergeConfig(
    base: UnifiedPostProcessConfig,
    override: Partial<UnifiedPostProcessConfig>,
  ): UnifiedPostProcessConfig {
    return {
      bloom: { ...base.bloom, ...override.bloom },
      chromaticAberration: {
        ...base.chromaticAberration,
        ...override.chromaticAberration,
      },
      motionBlur: { ...base.motionBlur, ...override.motionBlur },
      radialBlur: { ...base.radialBlur, ...override.radialBlur },
      colorGrade: { ...base.colorGrade, ...override.colorGrade },
      pixelation: { ...base.pixelation, ...override.pixelation },
      palette: { ...base.palette, ...override.palette },
      flash: { ...base.flash, ...override.flash },
      distortion: { ...base.distortion, ...override.distortion },
      heatDistortion: { ...base.heatDistortion, ...override.heatDistortion },
      screenShake: { ...base.screenShake, ...override.screenShake },
    };
  }
}

interface ShaderProgram {
  program: WebGLProgram;
  uniforms: Record<string, WebGLUniformLocation>;
  positionAttrib: number;
}
